import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Beyond Sure — Toast primitive (A1, portal).
 *
 * Provider + useToast() hook. Place <ToastProvider> at the app root, then:
 *
 *   const toast = useToast();
 *   toast.success("Policy saved.");
 *   toast.error("Couldn't reach the model — try again.");
 *   toast.info("Mistral excluded — using India-only providers.");
 *
 * Toasts stack bottom-right (top-right on mobile narrow). Default duration
 * 4 seconds; pass `{ duration: 0 }` for sticky toasts the user must
 * dismiss. Reduced motion → fade only.
 *
 * Accessibility:
 *   role="status" for non-error, role="alert" for error
 *   aria-live="polite" / "assertive"
 *   each toast has a close × that returns focus to the triggering element
 *
 * Implementation note: this is a controlled queue, not a stack with
 * priority. New toasts append; auto-dismiss removes by id.
 */

type Variant = "success" | "error" | "info" | "warn";

interface ToastEntry {
  id: number;
  variant: Variant;
  message: ReactNode;
  /** Auto-dismiss in ms. 0 = sticky. */
  duration: number;
}

interface ToastOptions {
  duration?: number;
}

interface ToastApi {
  success: (msg: ReactNode, opts?: ToastOptions) => void;
  error: (msg: ReactNode, opts?: ToastOptions) => void;
  info: (msg: ReactNode, opts?: ToastOptions) => void;
  warn: (msg: ReactNode, opts?: ToastOptions) => void;
  /** Dismiss all visible toasts. */
  clear: () => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

/* ────────────────── Hook ────────────────── */

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error("useToast() must be used inside <ToastProvider>");
  }
  return ctx;
}

/* ────────────────── Provider ────────────────── */

interface ToastProviderProps {
  children: ReactNode;
  /** Default auto-dismiss duration (ms). Default 4000. */
  defaultDuration?: number;
}

export function ToastProvider({
  children,
  defaultDuration = 4000,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);
  const reducedMotion = useReducedMotion();

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: Variant, message: ReactNode, opts?: ToastOptions) => {
      const id = ++idRef.current;
      const duration = opts?.duration ?? defaultDuration;
      setToasts((prev) => [...prev, { id, variant, message, duration }]);
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
    },
    [defaultDuration, dismiss],
  );

  const api: ToastApi = {
    success: (m, o) => push("success", m, o),
    error: (m, o) => push("error", m, o),
    info: (m, o) => push("info", m, o),
    warn: (m, o) => push("warn", m, o),
    clear: () => setToasts([]),
  };

  const portalRoot = typeof document !== "undefined" ? document.body : null;

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {portalRoot
        ? createPortal(
            <div
              className={[
                "fixed z-[1100] pointer-events-none",
                "top-3 right-3 sm:top-auto sm:bottom-4 sm:right-4",
                "flex flex-col items-stretch sm:items-end gap-2",
                "w-[calc(100vw-24px)] sm:w-auto sm:max-w-[380px]",
              ].join(" ")}
              aria-live="polite"
              aria-atomic={false}
            >
              <AnimatePresence>
                {toasts.map((t) => (
                  <ToastCard
                    key={t.id}
                    entry={t}
                    onDismiss={() => dismiss(t.id)}
                    reducedMotion={reducedMotion ?? false}
                  />
                ))}
              </AnimatePresence>
            </div>,
            portalRoot,
          )
        : null}
    </ToastCtx.Provider>
  );
}

/* ────────────────── Card ────────────────── */

function ToastCard({
  entry,
  onDismiss,
  reducedMotion,
}: {
  entry: ToastEntry;
  onDismiss: () => void;
  reducedMotion: boolean;
}) {
  const palette =
    entry.variant === "error"
      ? {
          bg: "bg-[rgb(214_64_64_/_0.06)]",
          border: "border-[rgb(214_64_64_/_0.32)]",
          ink: "text-[#9a3a3a]",
          icon: "✕",
        }
      : entry.variant === "warn"
      ? {
          bg: "bg-[rgb(199_132_17_/_0.08)]",
          border: "border-[rgb(199_132_17_/_0.32)]",
          ink: "text-[#8a6a18]",
          icon: "!",
        }
      : entry.variant === "info"
      ? {
          bg: "bg-[rgb(30_144_255_/_0.06)]",
          border: "border-[rgb(30_144_255_/_0.28)]",
          ink: "text-[var(--color-azure)]",
          icon: "i",
        }
      : {
          bg: "bg-[var(--color-brand-soft)]",
          border: "border-[var(--color-brand)]/30",
          ink: "text-[var(--color-brand-deep)]",
          icon: "✓",
        };

  return (
    <motion.div
      role={entry.variant === "error" ? "alert" : "status"}
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: reducedMotion ? 0.12 : 0.22, ease: [0.4, 0, 0.2, 1] }}
      className={[
        "pointer-events-auto",
        "rounded-[12px] px-3.5 py-3",
        "bg-[var(--color-paper)]",
        "border", palette.border,
        "shadow-[0_8px_24px_-12px_rgba(0,0,0,0.22)]",
        "flex items-start gap-2.5",
      ].join(" ")}
      data-toast-variant={entry.variant}
    >
      <span
        aria-hidden
        className={[
          "inline-flex items-center justify-center shrink-0",
          "w-6 h-6 rounded-full font-bold text-[12px]",
          palette.bg, palette.ink,
        ].join(" ")}
      >
        {palette.icon}
      </span>
      <div className={[
        "flex-1 min-w-0 text-[13.5px] leading-[1.4]",
        "text-[var(--color-ink)]",
      ].join(" ")}>
        {entry.message}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={[
          "shrink-0 w-6 h-6 -mr-1 rounded-[6px]",
          "inline-flex items-center justify-center",
          "text-[var(--color-ink-40)] hover:text-[var(--color-ink)] hover:bg-[var(--color-ink-08)]",
          "transition-colors duration-[var(--duration-fast)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-1",
        ].join(" ")}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M6 6L18 18M6 18L18 6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </motion.div>
  );
}

export default ToastProvider;
